/**
 * One chat (/chat/:chatId): messages polled every few seconds while the screen is visible, day
 * separators, a "Ný skilaboð" divider at what was unread when the chat opened, older pages on request,
 * and the composer at the bottom (sent through the outbox: offline the message waits, marked "Bíður
 * eftir neti"). The chat is marked read up to the newest message on screen while the page is visible.
 */
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, BellOff, MessagesSquare, SendHorizontal, WifiOff, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useParams } from 'react-router';
import { canAccessChat, CHAT_MESSAGE_MAX, isChatId, type ChatId } from '../../shared/chats';
import { uuid } from '../../shared/ids';
import { excerpt } from '../../shared/notifications';
import { useServerNow } from '../api/calendar';
import {
  discardLocalChatMessage,
  loadOlderMessages,
  markChatRead,
  onChatMessageRejected,
  sendChatMessage,
  useChatMessages,
  useChats,
  useDeleteChatMessage,
  useEditChatMessage,
  useMuteChat,
} from '../api/chat';
import { ApiFailure } from '../api/client';
import { dropNotification } from '../api/notifications';
import { useRequiredMe } from '../auth/AuthProvider';
import { Page } from '../components/layout';
import { useToast } from '../components/toast';
import { Button, CenteredSpinner, ConfirmSheet, cx, EmptyState, ErrorBox, IconButton } from '../components/ui';
import { useI18n, useT } from '../i18n';
import { dayHeading, todayStr } from '../lib/dates';
import { errorText, useIsEditor } from '../screens/calendar/shared';
import { ChatMessage } from './ChatMessage';
import { chatName } from './labels';
import { buildTimeline, newestFromOthers, type ChatItem } from './logic';

/** Within this many px of the bottom counts as "reading the newest": new messages scroll into view. */
const NEAR_BOTTOM_PX = 160;

const mainEl = () => document.getElementById('main');

function Composer({ replyTo, onCancelReply, onSend }: { replyTo: ChatItem | null; onCancelReply: () => void; onSend: (body: string) => void }) {
  const t = useT();
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [text]);

  useEffect(() => {
    if (replyTo) ref.current?.focus();
  }, [replyTo]);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body) return;
    onSend(body);
    setText('');
  };

  // ⌘/Ctrl+Enter always sends; plain Enter sends with a mouse / trackpad (Shift+Enter: new line). On touch
  // keyboards Enter is a new line.
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    if (e.metaKey || e.ctrlKey || (!e.shiftKey && window.matchMedia('(pointer: fine)').matches)) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <form
      onSubmit={submit}
      className="sticky bottom-0 z-10 -mx-4 mt-3 flex flex-col gap-2 border-t border-[var(--line)] bg-[var(--bg)] px-4 pt-3 pb-3 md:-mx-8 md:px-8 md:pb-[calc(var(--safe-bottom)+12px)]"
    >
      {replyTo ? (
        <div className="flex items-center gap-2 rounded-xl bg-[var(--accent-soft)] py-1 pr-1 pl-3 text-[15px] text-[var(--accent)]">
          <span className="min-w-0 flex-1 truncate font-semibold">{t('chat.replying_to', { name: replyTo.author.fullName })}</span>
          <IconButton icon={X} label={t('chat.cancel_reply')} onClick={onCancelReply} />
        </div>
      ) : null}
      <div className="flex items-end gap-2">
        <textarea
          ref={ref}
          rows={1}
          value={text}
          maxLength={CHAT_MESSAGE_MAX}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('chat.placeholder')}
          aria-label={t('chat.placeholder')}
          enterKeyHint="send"
          className="max-h-52 min-h-12 flex-1 resize-none rounded-2xl border border-[var(--line)] bg-[var(--surface)] px-4 py-2.5 text-[17px] text-[var(--ink)] placeholder:text-[var(--muted)] focus:border-[var(--focus)] focus:ring-2 focus:ring-[var(--focus)]/30 focus:outline-none"
        />
        <Button type="submit" icon={SendHorizontal} disabled={!text.trim()} className="min-h-12 shrink-0">
          <span className="hidden sm:inline">{t('chat.send')}</span>
          <span className="sr-only sm:hidden">{t('chat.send')}</span>
        </Button>
      </div>
    </form>
  );
}

/** Toast when a message queued offline was refused later (it stays in the chat marked "Ekki sent"). */
function useRejectionToasts(): void {
  const toast = useToast();
  const t = useT();
  const ref = useRef({ toast, t });
  useEffect(() => {
    ref.current = { toast, t };
  });
  useEffect(
    () =>
      onChatMessageRejected((_chatId, error) => {
        const { toast: tt, t: tr } = ref.current;
        tt.show(`${tr('chat.rejected')} ${errorText(tr, error)}`, { tone: 'error', sticky: true });
      }),
    [],
  );
}

function ChatView({ chatId }: { chatId: ChatId }) {
  const t = useT();
  const { loc } = useI18n();
  const me = useRequiredMe();
  const editor = useIsEditor();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useChatMessages(chatId);
  const summary = useChats({ poll: false, staleTime: 60_000 }).data?.find((c) => c.id === chatId);
  const mute = useMuteChat();
  const edit = useEditChatMessage(chatId);
  const del = useDeleteChatMessage(chatId);
  const now = useServerNow(30_000);
  const [replyTo, setReplyTo] = useState<ChatItem | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ChatItem | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [showJump, setShowJump] = useState(false);
  useRejectionToasts();

  const gone = q.error instanceof ApiFailure && (q.error.status === 404 || q.error.status === 403);
  const thread = gone ? undefined : q.data;
  const messages = useMemo(() => thread?.messages ?? [], [thread]);

  // What was unread when the chat opened gets the "Ný skilaboð" divider for the whole visit.
  const openedAfter = useRef<number | null>(null);
  if (openedAfter.current === null && thread) openedAfter.current = thread.unreadAfter;
  const timeline = useMemo(() => buildTimeline(messages, me.id, openedAfter.current), [messages, me.id]);
  const today = todayStr(now);

  // ── Read marker: up to the newest message by someone else, while the page is visible ──
  const newest = newestFromOthers(messages, me.id);
  const newestAt = newest?.createdAt ?? 0;
  const unreadAfter = thread?.unreadAfter ?? 0;
  const marked = useRef(0);
  useEffect(() => {
    if (!newestAt) return;
    const tryMark = () => {
      if (document.visibilityState !== 'visible' || newestAt <= marked.current) return;
      marked.current = newestAt;
      if (newestAt > unreadAfter) void markChatRead(chatId, newestAt, qc);
      else dropNotification(qc, 'chat', chatId, newestAt);
    };
    tryMark();
    document.addEventListener('visibilitychange', tryMark);
    return () => document.removeEventListener('visibilitychange', tryMark);
  }, [chatId, newestAt, unreadAfter, qc]);

  // ── Scrolling: open at the divider (or the bottom); follow new messages while near the bottom ──
  const nearBottom = useRef(true);
  const olderAnchor = useRef<{ height: number; top: number } | null>(null);
  const positioned = useRef(false);
  const hasThread = !!thread;
  useEffect(() => {
    const main = mainEl();
    if (!main) return;
    const onScroll = () => {
      nearBottom.current = main.scrollHeight - main.scrollTop - main.clientHeight < NEAR_BOTTOM_PX;
      if (nearBottom.current) setShowJump(false);
    };
    main.addEventListener('scroll', onScroll, { passive: true });
    return () => main.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    if (!hasThread || positioned.current) return;
    positioned.current = true;
    // After the shell's own scroll handling for this navigation (it runs in a layout effect).
    const id = requestAnimationFrame(() => {
      const main = mainEl();
      if (!main) return;
      const divider = document.getElementById('chat-new');
      if (divider) divider.scrollIntoView({ block: 'center' });
      else main.scrollTop = main.scrollHeight;
      nearBottom.current = main.scrollHeight - main.scrollTop - main.clientHeight < NEAR_BOTTOM_PX;
    });
    return () => cancelAnimationFrame(id);
  }, [hasThread]);

  const lastId = messages.at(-1)?.id;
  const lastMine = messages.at(-1)?.author.id === me.id;
  const firstId = messages[0]?.id;
  useLayoutEffect(() => {
    const main = mainEl();
    if (!main || !positioned.current) return;
    const anchor = olderAnchor.current;
    if (anchor) {
      // An older page was added above: keep the same messages in view.
      olderAnchor.current = null;
      main.scrollTop = anchor.top + (main.scrollHeight - anchor.height);
      return;
    }
    if (nearBottom.current || lastMine) {
      main.scrollTop = main.scrollHeight;
      nearBottom.current = true;
    } else setShowJump(true);
  }, [lastId, firstId, lastMine]);

  const jumpToLatest = () => {
    const main = mainEl();
    if (main) main.scrollTo({ top: main.scrollHeight, behavior: 'smooth' });
    setShowJump(false);
  };

  const loadOlder = async () => {
    const main = mainEl();
    if (main) olderAnchor.current = { height: main.scrollHeight, top: main.scrollTop };
    setLoadingOlder(true);
    try {
      await loadOlderMessages(qc, chatId);
    } catch (e) {
      olderAnchor.current = null;
      toast.show(errorText(t, e), { tone: 'error' });
    } finally {
      setLoadingOlder(false);
    }
  };

  // ── Writing ──
  const author = { id: me.id, fullName: me.fullName, section: me.section, title: me.title, avatarRev: me.avatarRev, inactive: false };
  const send = async (body: string, reply: ChatItem | null, id = uuid()) => {
    nearBottom.current = true;
    const replyRef = reply ? { id: reply.id, authorName: reply.author.fullName, excerpt: reply.body === null ? null : excerpt(reply.body) } : null;
    try {
      const r = await sendChatMessage(chatId, { id, body, replyTo: reply?.id ?? null }, author, replyRef, qc);
      if (r === 'queued') toast.show(t('chat.saved_offline'));
    } catch (e) {
      toast.show(errorText(t, e), { tone: 'error' });
    }
  };
  const retry = (m: ChatItem) => {
    const replyRef = m.replyTo;
    nearBottom.current = true;
    void sendChatMessage(chatId, { id: m.id, body: m.body ?? '', replyTo: m.replyTo?.id ?? null }, author, replyRef, qc).then(
      (r) => r === 'queued' && toast.show(t('chat.saved_offline')),
      (e: unknown) => toast.show(errorText(t, e), { tone: 'error' }),
    );
  };
  const onEdit = async (id: string, body: string) => {
    try {
      await edit.mutateAsync({ id, body });
      return true;
    } catch (e) {
      toast.show(errorText(t, e), { tone: 'error' });
      return false;
    }
  };

  const toggleMute = () => {
    if (!summary) return;
    const muted = !summary.muted;
    mute.mutate(
      { chatId, muted },
      {
        onSuccess: () => toast.show(t(muted ? 'chat.muted_done' : 'chat.unmuted_done')),
        onError: (e) => toast.show(errorText(t, e), { tone: 'error' }),
      },
    );
  };

  const name = chatName(chatId, t);
  // A labelled toggle (not a bare bell icon): the notification bell sits right next to it.
  const actions = summary ? (
    <button
      type="button"
      aria-pressed={summary.muted}
      aria-label={t('chat.mute')}
      title={t('chat.mute')}
      disabled={mute.isPending}
      onClick={toggleMute}
      className={cx(
        'inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-xl border px-2.5 text-[15px] font-semibold transition disabled:opacity-50 sm:px-3',
        summary.muted
          ? 'border-transparent bg-[var(--accent-soft)] text-[var(--accent)]'
          : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink-2)] hover:bg-[var(--surface-2)]',
      )}
    >
      <BellOff className="size-5" aria-hidden />
      <span className="hidden sm:inline">{t('chat.mute_short')}</span>
    </button>
  ) : undefined;

  let body;
  if (gone) {
    body = (
      <EmptyState icon={MessagesSquare} title={t('error.not_found')}>
        {t('chat.not_found')}
      </EmptyState>
    );
  } else if (!thread) {
    body =
      q.isPending && q.fetchStatus === 'paused' ? (
        <EmptyState icon={WifiOff} title={t('common.offline')}>
          {t('common.no_data_offline')}
        </EmptyState>
      ) : q.isPending ? (
        <CenteredSpinner label={t('common.loading')} />
      ) : (
        <ErrorBox onRetry={() => void q.refetch()} retryLabel={t('common.retry')}>
          {t('chat.load_error')}
        </ErrorBox>
      );
  } else {
    body = (
      <>
        {/* Few messages sit at the bottom, next to the composer. */}
        <div className="flex-1" />
        {thread.hasOlder ? (
          <div className="mb-2 flex justify-center">
            <Button variant="secondary" size="sm" className="min-h-11" busy={loadingOlder} onClick={() => void loadOlder()}>
              {t('chat.load_older')}
            </Button>
          </div>
        ) : null}
        {timeline.length === 0 ? (
          <p className="py-10 text-center text-[var(--muted)]">{t('chat.empty')}</p>
        ) : (
          <ul role="log" aria-label={t('chat.messages')} aria-live="polite" className="flex flex-col pb-1">
            {timeline.map((e) =>
              e.type === 'day' ? (
                <li key={e.key} className="mt-4 mb-1 flex justify-center">
                  <span className="rounded-full bg-[var(--surface-2)] px-3 py-1 text-[13px] font-semibold text-[var(--ink-2)]">{dayHeading(e.date, today, loc)}</span>
                </li>
              ) : e.type === 'new' ? (
                <li key={e.key} id="chat-new" className="mt-4 flex scroll-mt-24 items-center gap-3 text-[13px] font-bold text-[var(--danger-text)]">
                  <span className="h-px flex-1 bg-[var(--danger)]" aria-hidden />
                  {t('chat.new_divider')}
                  <span className="h-px flex-1 bg-[var(--danger)]" aria-hidden />
                </li>
              ) : (
                <ChatMessage
                  key={e.key}
                  m={e.m}
                  mine={e.mine}
                  first={e.first}
                  last={e.last}
                  showSection={chatId === 'choir'}
                  selected={selected === e.m.id}
                  canDelete={editor}
                  onSelect={setSelected}
                  onReply={setReplyTo}
                  onEdit={onEdit}
                  onDelete={setConfirmDelete}
                  onRetry={retry}
                  onDiscard={(m) => discardLocalChatMessage(chatId, m.id, qc)}
                />
              ),
            )}
          </ul>
        )}
        {showJump ? (
          <div className="sticky bottom-24 z-10 flex justify-center">
            <Button size="sm" icon={ArrowDown} className="min-h-11 rounded-full shadow-lg" onClick={jumpToLatest}>
              {t('chat.jump_latest')}
            </Button>
          </div>
        ) : null}
        <Composer
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          onSend={(text) => {
            const reply = replyTo;
            setReplyTo(null);
            void send(text, reply);
          }}
        />
      </>
    );
  }

  return (
    <Page title={name} back="/chat" actions={actions} fill>
      {body}
      <ConfirmSheet
        open={!!confirmDelete}
        title={t('chat.delete_title')}
        body={t('chat.delete_body')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        danger
        busy={del.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          const target = confirmDelete;
          if (!target) return;
          del.mutate(target.id, {
            onSuccess: () => setConfirmDelete(null),
            onError: (e) => {
              setConfirmDelete(null);
              toast.show(errorText(t, e), { tone: 'error' });
            },
          });
        }}
      />
    </Page>
  );
}

export default function ChatScreen() {
  const { chatId = '' } = useParams();
  const me = useRequiredMe();
  const t = useT();
  if (!isChatId(chatId) || !canAccessChat(me, chatId)) {
    return (
      <Page title={t('chat.title')} back="/chat">
        <EmptyState icon={MessagesSquare} title={t('error.not_found')}>
          {t('chat.not_found')}
        </EmptyState>
      </Page>
    );
  }
  return <ChatView key={chatId} chatId={chatId} />;
}
